import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
    PageHeader, Card, LoadingState, EmptyState, ErrorState, Pill,
    CopperButton, SlateButton, Modal, Field, TextInput, NumberInput, Select,
} from "@/components/common/UI";
import { useAuth, hasRole } from "@/lib/auth";
import { Package, Plus, Trash2 } from "lucide-react";
import { fmtINR } from "@/lib/format";
import { toast } from "sonner";
import { ImportExportBar } from "@/components/foundation/ImportExportBar";

const CATEGORIES = ["GENERIC", "PAINT", "HYDRAULIC", "FASTENER", "ELECTRICAL", "RUBBER"];
const UOMS = ["PER_PIECE", "PER_KG", "PER_SET", "PER_LITRE"];
const EMPTY_FORM = { code: "", name: "", category: "GENERIC", rate: "", uom: "PER_PIECE", supplier_id: "", is_active: true };

export default function BOPCatalogue() {
    const { user } = useAuth();
    const [rows, setRows] = useState(null);
    const [suppliers, setSuppliers] = useState([]);
    const [error, setError] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [submitting, setSubmitting] = useState(false);
    const canEdit = hasRole(user, "CORPORATE_ADMIN");

    const load = () => {
        api.get("/bop-price-catalogue", { params: categoryFilter ? { category: categoryFilter } : {} })
            .then((r) => setRows(r.data))
            .catch((e) => setError(formatApiError(e)));
    };

    useEffect(() => { load(); }, [categoryFilter]);
    useEffect(() => { api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {}); }, []);

    const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setModalOpen(true); };
    const openEdit = (row) => {
        setEditing(row);
        setForm({
            code: row.code, name: row.name, category: row.category, rate: row.rate,
            uom: row.uom || "PER_PIECE", supplier_id: row.supplier_id || "", is_active: row.is_active,
        });
        setModalOpen(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = { ...form, rate: parseFloat(form.rate) || 0, supplier_id: form.supplier_id || null };
            if (editing) await api.put(`/bop-price-catalogue/${editing.id}`, payload);
            else await api.post("/bop-price-catalogue", payload);
            toast.success(editing ? "BOP catalogue item updated" : "BOP catalogue item created");
            setModalOpen(false);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    const remove = async (row) => {
        if (!window.confirm(`Delete BOP catalogue item ${row.code}? This action cannot be undone.`)) return;
        try {
            await api.delete(`/bop-price-catalogue/${row.id}`);
            toast.success("Deleted");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div data-testid="bop-catalogue-page">
            <PageHeader
                eyebrow="FOUNDATION · MASTERS"
                title="BOP Catalogue"
                subtitle="A reusable price book for bought-out parts: paint, hydraulic kits, fasteners, electricals, and general items. Picked from here on the RFQ form instead of typed as a lump sum, and bulk-loadable via .xlsx."
                actions={
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                        <ImportExportBar entity="bop-catalogue" label="BOP Catalogue" canImport={canEdit} onImported={load} />
                        {canEdit && (
                            <CopperButton onClick={openCreate} data-testid="bop-catalogue-add-btn">
                                <Plus className="h-4 w-4" /> Add Item
                            </CopperButton>
                        )}
                    </div>
                }
            />

            <Card className="p-3 mb-4">
                <Field label="Filter by category">
                    <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="h-8 text-[12px] max-w-xs">
                        <option value="">All categories</option>
                        {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </Select>
                </Field>
            </Card>

            {error && <ErrorState message={error} onRetry={load} />}
            {!error && rows === null && <LoadingState />}

            {rows && rows.length === 0 && (
                <EmptyState icon={Package} title="No BOP catalogue items yet" description="Add your first item, or download the template to bulk-import a real price book." />
            )}

            {rows && rows.length > 0 && (
                <Card>
                    <table className="w-full table-rowhover">
                        <thead>
                            <tr className="border-b border-slate-200 bg-slate-50/60">
                                <th className="eyebrow text-left py-2 px-4">Code</th>
                                <th className="eyebrow text-left py-2 px-4">Name</th>
                                <th className="eyebrow text-left py-2 px-4">Category</th>
                                <th className="eyebrow text-right py-2 px-4">Rate</th>
                                <th className="eyebrow text-left py-2 px-4">UOM</th>
                                <th className="eyebrow text-left py-2 px-4">Status</th>
                                <th className="eyebrow text-right py-2 px-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id} className="border-b border-slate-100">
                                    <td className="py-1.5 px-4 text-[13px] font-mono font-semibold text-slate-900">{r.code}</td>
                                    <td className="py-1.5 px-4 text-[13px]">{r.name}</td>
                                    <td className="py-1.5 px-4"><Pill tone="copper">{r.category}</Pill></td>
                                    <td className="py-1.5 px-4 text-right font-mono tabular-nums text-[13px]">{fmtINR(r.rate, 2)}</td>
                                    <td className="py-1.5 px-4 text-[12px] text-slate-500">{r.uom}</td>
                                    <td className="py-1.5 px-4">
                                        <Pill tone={r.is_active ? "emerald" : "slate"}>{r.is_active ? "Active" : "Inactive"}</Pill>
                                    </td>
                                    <td className="py-1.5 px-4 text-right">
                                        {canEdit && (
                                            <div className="inline-flex gap-1">
                                                <SlateButton onClick={() => openEdit(r)} className="h-7 px-2.5 text-xs">Edit</SlateButton>
                                                <SlateButton
                                                    onClick={() => remove(r)}
                                                    variant="ghost"
                                                    className="h-7 px-2 text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
                                                    title={`Delete ${r.code}`}
                                                >
                                                    <Trash2 className="h-3 w-3" /> Delete
                                                </SlateButton>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Card>
            )}

            <Modal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                title={editing ? `Edit BOP Item — ${editing.code}` : "Add New BOP Catalogue Item"}
                footer={
                    <>
                        <SlateButton variant="outline" onClick={() => setModalOpen(false)}>Cancel</SlateButton>
                        <CopperButton onClick={submit} disabled={submitting}>
                            {submitting ? "Saving…" : (editing ? "Save Changes" : "Create Item")}
                        </CopperButton>
                    </>
                }
            >
                <form onSubmit={submit} className="space-y-5">
                    <Field label="Code" required helper="Short unique code, e.g. PAINT-TOPCOAT-RAL">
                        <TextInput value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} required disabled={!!editing} />
                    </Field>
                    <Field label="Name" required>
                        <TextInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                    </Field>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Category" required>
                            <Select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                            </Select>
                        </Field>
                        <Field label="UOM">
                            <Select value={form.uom} onChange={(e) => setForm({ ...form, uom: e.target.value })}>
                                {UOMS.map((u) => <option key={u} value={u}>{u}</option>)}
                            </Select>
                        </Field>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Rate (₹)" required>
                            <NumberInput value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} step="0.01" required />
                        </Field>
                        <Field label="Supplier" helper="Optional">
                            <Select value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })}>
                                <option value="">None</option>
                                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </Select>
                        </Field>
                    </div>
                    <Field label="Status">
                        <Select value={form.is_active ? "active" : "inactive"} onChange={(e) => setForm({ ...form, is_active: e.target.value === "active" })}>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </Select>
                    </Field>
                </form>
            </Modal>
        </div>
    );
}
